import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Grid,
    Typography,
    Alert,
    Divider,
    IconButton,
    Modal,
    Tooltip,
    Switch,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Popper,
    ClickAwayListener
} from '@mui/material';
import HelpIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
    useGetCitiesByTenancyIdQuery,
    useGetCompanyNamesByTenancyIdQuery,
    useGetCountriesByTenancyIdQuery,
    useGetDepartmentsByTenancyIdQuery,
    useGetLocationsByTenancyIdQuery,
    useGetStatesByTenancyIdQuery,
} from '../../../store/services/tenancy.service';
import { 
    useGetUsersQuery, 
    useUpdateUserRoleMutation 
} from '../../../store/services/user-service';
import MainCard from '../../../components/cards/MainCard';
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../../utils/colorUtils";

AdminsSelectionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    state: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onSave: PropTypes.func,
};

export default function AdminsSelectionModal({ open, onClose, state, onChange, onSave }) {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const [errorMessage, setErrorMessage] = useState('');
    const [currentAdmin, setCurrentAdmin] = useState({
        firstName: '',
        lastName: '',
        email: '',
        location: '',
        department: '',
        companyName: '',
        country: '',
        state: '',
        city: '',
    });
    const [errors, setErrors] = useState({});
    const [currentUser, setCurrentUser] = useState(null);

    // User search and promotion states
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [selectedUserToPromote, setSelectedUserToPromote] = useState(null);
    const [searchAnchorEl, setSearchAnchorEl] = useState(null);
    const [isPromotingUser, setIsPromotingUser] = useState(false);
    const [searchInputRef, setSearchInputRef] = useState(null);
    
    // New state for promoted users
    const [promotedUsers, setPromotedUsers] = useState([]);
    const [demotingUsers, setDemotingUsers] = useState(new Set());

    const { data: departments } = useGetDepartmentsByTenancyIdQuery({ tenancyId });
    const { data: locations } = useGetLocationsByTenancyIdQuery({ tenancyId });
    const { data: cities } = useGetCitiesByTenancyIdQuery({ tenancyId });
    const { data: companyNames } = useGetCompanyNamesByTenancyIdQuery({ tenancyId });
    const { data: countries } = useGetCountriesByTenancyIdQuery({ tenancyId });
    const { data: states } = useGetStatesByTenancyIdQuery({ tenancyId });

    // User search query and role update mutation
    const { data: usersData, refetch: refetchUsers } = useGetUsersQuery({
        perPage: 50,
        page: 0,
        searchTerm: userSearchQuery,
        tenancyId: tenancyId,
        adminFilter: false
    }, { skip: userSearchQuery.length < 2 });

    const [updateUserRole] = useUpdateUserRoleMutation();

    // Load the current user from localStorage when component mounts
    useEffect(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            setCurrentUser(user);
        } catch (error) {
            console.error("Error retrieving user from localStorage:", error);
        }
    }, []);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open) {
            setCurrentAdmin({
                firstName: '',
                lastName: '',
                email: '',
                location: '',
                department: '',
                companyName: '',
                country: '',
                state: '',
                city: '',
            });
            setErrors({});
            setErrorMessage('');
            // Reset user search states
            setUserSearchQuery('');
            setUserSearchResults([]);
            setSelectedUserToPromote(null);
            setSearchAnchorEl(null);
            // Reset promoted users
            setPromotedUsers([]);
            setDemotingUsers(new Set());
        }
    }, [open]);

    // Handle user search results
    useEffect(() => {
        if (usersData && usersData.data) {
            // Filter out users who are already admins, the current user, and promoted users
            const promotedUserIds = promotedUsers.map(user => user.id);
            const filteredUsers = usersData.data.filter(user => 
                !user.role?.includes("TenancyAdministrators") &&
                user.role !== "Administrators" &&
                user.enabled &&
                user.email.toLowerCase() !== currentUser?.email?.toLowerCase() &&
                !promotedUserIds.includes(user.id)
            );
            setUserSearchResults(filteredUsers);
        } else {
            setUserSearchResults([]);
        }
    }, [usersData, currentUser, promotedUsers]);

    const validateEmail = (email) => {
        const re = /\S+@\S+\.\S+/;
        return re.test(email);
    };

    const validateCurrentAdmin = () => {
        const newErrors = {};

        if (!currentAdmin.firstName) {
            newErrors.firstName = true;
        }
        if (!currentAdmin.email) {
            newErrors.email = true;
        } else if (!validateEmail(currentAdmin.email)) {
            newErrors.email = true;
            setErrorMessage('Please enter a valid email address');
            return false;
        }

        // Check if the email is the same as the current user's email
        if (currentUser && currentAdmin.email.toLowerCase() === currentUser.email.toLowerCase()) {
            newErrors.email = true;
            setErrorMessage('You cannot add yourself as an administrator. You already have access to this tenancy.');
            return false;
        }

        // Check if the email already exists in the list of admins
        if (state.assignAdmins?.admins?.some(admin =>
            admin.email.toLowerCase() === currentAdmin.email.toLowerCase()
        )) {
            newErrors.email = true;
            setErrorMessage('This email address has already been added to the administrators list.');
            return false;
        }

        // Check if the email already exists in the promoted users list
        if (promotedUsers.some(user =>
            user.email.toLowerCase() === currentAdmin.email.toLowerCase()
        )) {
            newErrors.email = true;
            setErrorMessage('This user has already been promoted to administrator.');
            return false;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddAdmin = () => {
        if (!validateCurrentAdmin()) {
            return;
        }

        const newAdmin = {
            ...currentAdmin,
            userName: currentAdmin.email,
            startUserJourneyOn: new Date().toISOString(),
            tenancyId,
            userSource: "MANUAL",
            upn: null
        };

        const updatedAdmins = [...(state.assignAdmins.admins || []), newAdmin];
        onChange('assignAdmins', { admins: updatedAdmins });

        // Reset current admin form
        setCurrentAdmin({
            firstName: '',
            lastName: '',
            email: '',
            location: '',
            department: '',
            companyName: '',
            country: '',
            state: '',
            city: '',
        });
        setErrors({});
        setErrorMessage('');
    };

    const handleRemoveAdmin = (index) => {
        const updatedAdmins = state.assignAdmins.admins.filter((_, i) => i !== index);
        onChange('assignAdmins', { admins: updatedAdmins });
    };

    const handleDemoteUser = async (user, index) => {
        setDemotingUsers(prev => new Set([...prev, user.id]));
        
        try {
            await updateUserRole({
                userId: user.id,
                role: "Users" // or whatever the default role should be
            }).unwrap();

            // Remove from promoted users list
            const updatedPromotedUsers = promotedUsers.filter((_, i) => i !== index);
            setPromotedUsers(updatedPromotedUsers);
            
            setErrorMessage('');
        } catch (error) {
            console.error("Error demoting user:", error);
            setErrorMessage(`Error demoting ${user.firstName} ${user.lastName} from admin role. Please try again.`);
        } finally {
            setDemotingUsers(prev => {
                const newSet = new Set([...prev]);
                newSet.delete(user.id);
                return newSet;
            });
        }
    };

    const handleInputChange = (field, value) => {
        setCurrentAdmin(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: false
            }));
        }

        // Clear error message when email changes
        if (field === 'email') {
            setErrorMessage('');
        }
    };

    // User search and promotion handlers
    const handleUserSearchChange = (event) => {
        const query = event.target.value;
        setUserSearchQuery(query);
        setSearchAnchorEl(event.target);
        
        if (query.length < 2) {
            setUserSearchResults([]);
            setSearchAnchorEl(null);
        }
    };

    const handleSelectUserToPromote = (user) => {
        setSelectedUserToPromote(user);
        setUserSearchQuery(`${user.firstName} ${user.lastName} (${user.email})`);
        setUserSearchResults([]);
        setSearchAnchorEl(null);
    };

    const handleUserSearchClickAway = () => {
        setSearchAnchorEl(null);
        setUserSearchResults([]);
    };

    const handlePromoteUserToAdmin = async () => {
        if (!selectedUserToPromote) return;

        setIsPromotingUser(true);
        try {
            await updateUserRole({
                userId: selectedUserToPromote.id,
                role: "TenancyAdministrators"
            }).unwrap();

            // Add the promoted user to the promoted users list
            setPromotedUsers(prev => [...prev, {
                ...selectedUserToPromote,
                promotedAt: new Date().toISOString()
            }]);

            setErrorMessage('');
            // Show success message by updating the search field
            setUserSearchQuery(`${selectedUserToPromote.firstName} ${selectedUserToPromote.lastName} promoted to admin!`);
            
            // Reset after 2 seconds
            setTimeout(() => {
                setSelectedUserToPromote(null);
                setUserSearchQuery('');
            }, 2000);

        } catch (error) {
            console.error("Error promoting user to admin:", error);
            setErrorMessage(`Error promoting ${selectedUserToPromote.firstName} ${selectedUserToPromote.lastName} to admin. Please try again.`);
        } finally {
            setIsPromotingUser(false);
        }
    };

    const clearUserSearch = () => {
        setUserSearchQuery('');
        setSelectedUserToPromote(null);
        setUserSearchResults([]);
        setSearchAnchorEl(null);
    };

    const handleClose = () => {
        setCurrentAdmin({
            firstName: '',
            lastName: '',
            email: '',
            location: '',
            department: '',
            companyName: '',
            country: '',
            state: '',
            city: '',
        });
        setErrors({});
        setErrorMessage('');
        // Clear user search states
        setUserSearchQuery('');
        setSelectedUserToPromote(null);
        setUserSearchResults([]);
        setSearchAnchorEl(null);
        // Clear promoted users
        setPromotedUsers([]);
        setDemotingUsers(new Set());
        onClose();
    };

    const handleSave = () => {
        const manualAdminsCount = state.assignAdmins?.admins?.length || 0;
        const promotedUsersCount = promotedUsers.length;
        
        if (onSave) {
            onSave({
                manualAdminsCount,
                promotedUsersCount,
                totalCount: manualAdminsCount + promotedUsersCount
            });
        }
        handleClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="assign-admins-modal-title"
            aria-describedby="assign-admins-modal-description"
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: 800,
                    maxHeight: "90vh",
                    overflow: "auto",
                    boxShadow: theme.customShadows.z1,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: theme.borderRadius.sm,
                    p: "26px 32px"
                }}
            >
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SupervisorAccountIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography id="assign-admins-modal-title" variant="h4">
                            Assign Administrators
                        </Typography>
                    </Box>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {/* Content */}
                <Grid container spacing={2}>
                    {/* Add New Admin Section */}
                    <Grid item xs={12}>
                        <MainCard
                            content={true}
                            boxShadow={false}
                            border={true}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Tooltip title="Add administrators who will have full access to manage this tenancy. You cannot add yourself as you already have access to this tenancy.">
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main", fontSize: '1.2rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="h6">
                                        Add Administrator
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ width: "100%", mb: 2 }} />

                            {errorMessage && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                                    {errorMessage}
                                </Alert>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="First Name"
                                        value={currentAdmin.firstName}
                                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        required
                                        error={errors.firstName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Last Name"
                                        value={currentAdmin.lastName}
                                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        value={currentAdmin.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        required
                                        error={errors.email}
                                        helperText={currentUser?.email ? `Note: You cannot use your own email (${currentUser.email})` : ""}
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddAdmin}
                                    startIcon={<AddIcon />}
                                    size="small"
                                >
                                    Add Administrator
                                </Button>
                            </Box>
                        </MainCard>
                    </Grid>

                    {/* Added Admins Section */}
                    <Grid item xs={12}>
                        <MainCard
                            content={true}
                            boxShadow={false}
                            border={true}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                                maxHeight: '300px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Tooltip title="List of administrators who will be added to this tenancy.">
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main", fontSize: '1.2rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="h6">
                                        Added Administrators ({state.assignAdmins?.admins?.length || 0})
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ width: "100%", mb: 2 }} />

                            <Box sx={{ 
                                flexGrow: 1, 
                                overflow: 'auto',
                                maxHeight: '200px'
                            }}>
                                {state.assignAdmins?.admins?.length > 0 ? (
                                    state.assignAdmins.admins.map((admin, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="subtitle2">
                                                    {admin.firstName} {admin.lastName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {admin.email}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => handleRemoveAdmin(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ textAlign: 'center', py: 2 }}
                                    >
                                        No administrators added yet
                                    </Typography>
                                )}
                            </Box>
                        </MainCard>
                    </Grid>

                    {/* Promote Existing User Section */}
                    <Grid item xs={12}>
                        <MainCard
                            content={true}
                            boxShadow={false}
                            border={true}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Tooltip title="Search for existing users in your tenancy and promote them to administrator role.">
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main", fontSize: '1.2rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="h6">
                                        Promote Existing User
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ width: "100%", mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <ClickAwayListener onClickAway={handleUserSearchClickAway}>
                                        <Box sx={{ position: 'relative' }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Search for user to promote"
                                                value={userSearchQuery}
                                                onChange={handleUserSearchChange}
                                                placeholder="Type name or email..."
                                                InputProps={{
                                                    endAdornment: userSearchQuery && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={clearUserSearch}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    ),
                                                }}
                                            />
                                            <Popper
                                                open={userSearchResults.length > 0 && Boolean(searchAnchorEl)}
                                                anchorEl={searchAnchorEl}
                                                placement="bottom-start"
                                                style={{ width: searchAnchorEl?.offsetWidth, zIndex: 1300 }}
                                            >
                                                <Paper
                                                    elevation={3}
                                                    sx={{
                                                        mt: 0.5,
                                                        maxHeight: 200,
                                                        overflow: 'auto',
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <List sx={{ p: 0 }}>
                                                        {userSearchResults.map((user) => (
                                                            <ListItem
                                                                key={user.id}
                                                                button
                                                                onClick={() => handleSelectUserToPromote(user)}
                                                                sx={{
                                                                    '&:hover': {
                                                                        bgcolor: 'action.hover'
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={`${user.firstName} ${user.lastName}`}
                                                                    secondary={user.email}
                                                                    primaryTypographyProps={{ fontWeight: 'medium' }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Paper>
                                            </Popper>
                                        </Box>
                                    </ClickAwayListener>
                                </Grid>

                                {selectedUserToPromote && (
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            bgcolor: 'background.paper'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {selectedUserToPromote.firstName} {selectedUserToPromote.lastName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {selectedUserToPromote.email}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Current Role: {selectedUserToPromote.role || 'User'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                                        Promote to Admin
                                                    </Typography>
                                                    <Switch
                                                        checked={false}
                                                        onChange={handlePromoteUserToAdmin}
                                                        disabled={isPromotingUser}
                                                        color="primary"
                                                    />
                                                    {isPromotingUser && (
                                                        <CircularProgress size={20} sx={{ ml: 1 }} />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </MainCard>
                    </Grid>

                    {/* Promoted Users Section */}
                    <Grid item xs={12}>
                        <MainCard
                            content={true}
                            boxShadow={false}
                            border={true}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                                maxHeight: '300px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Tooltip title="List of existing users who have been promoted to administrator role during this session.">
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main", fontSize: '1.2rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="h6">
                                        Promoted Users ({promotedUsers.length})
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ width: "100%", mb: 2 }} />

                            <Box sx={{ 
                                flexGrow: 1, 
                                overflow: 'auto',
                                maxHeight: '200px'
                            }}>
                                {promotedUsers.length > 0 ? (
                                    promotedUsers.map((user, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                border: '1px solid',
                                                borderColor: 'success.main',
                                                bgcolor: 'success.50',
                                                borderRadius: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon 
                                                    sx={{ 
                                                        color: 'success.main', 
                                                        fontSize: '1.2rem', 
                                                        mr: 1 
                                                    }} 
                                                />
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        {user.firstName} {user.lastName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                                        Promoted to Administrator
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ mr: 1 }}>
                                                    Administrator
                                                </Typography>
                                                <Switch
                                                    checked={true}
                                                    onChange={() => handleDemoteUser(user, index)}
                                                    disabled={demotingUsers.has(user.id)}
                                                    color="success"
                                                    size="small"
                                                />
                                                {demotingUsers.has(user.id) && (
                                                    <CircularProgress size={20} sx={{ ml: 1 }} />
                                                )}
                                            </Box>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ textAlign: 'center', py: 2 }}
                                    >
                                        No users have been promoted yet
                                    </Typography>
                                )}
                            </Box>
                        </MainCard>
                    </Grid>
                </Grid>

                {/* Actions */}
                <Box mt={3} display="flex" justifyContent="flex-end" spacing={2}>
                    <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        variant="contained" 
                        color="primary"
                        disabled={!state.assignAdmins?.admins?.length && promotedUsers.length === 0}
                    >
                        Save ({(state.assignAdmins?.admins?.length || 0) + promotedUsers.length} Total)
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}